import { fill, t, type StringKey } from '../strings';

/**
 * 🔒 The seeded capability keys (piece 8; decision 10300, T769): the strings the
 * thirteen Modules cannot express, read by `canUseCapability`. Every
 * `WorkQueue.permissionKey` of the shell registry is one, and so is every key a
 * decision names as a role's. The seeded Admin role carries all of them, and the
 * role editor draws one tick per key, a key no role holds among them. A key
 * outside this list is refused by the role's save.
 */
export const CAPABILITY_KEYS = [
  'approvals.view',
  'mail.quarantine.review',
  'files.classify',
  'locations.edit',
  'leads.review',
  'nda.review',
  'nda.activate',
  'hr.orgchart.edit',
  'hr.hire',
  'hr.discipline.deliver',
  'creditnote.receive',
  'admin.operations',
  'time.approve',
  'deals.commission.edit',
  'documents.inbox.review',
  'duplicates.review',
  'records.dataquality.review',
  'todos.role.view',
  'accounting.post',
  'site.addForAnyAccount',
  'reports.projectperformance.view',
  'vendor.w9.override',
  // Piece 18 with piece 8, decision 1466: a costed change to a bill of materials after its Quote went out, saved with a typed reason instead of the document.
  'bom.costChange.override',
  // Piece 10 with piece 8, decision 1500 (T728): an administrator deletes a link made by mistake, from its row, with a typed reason.
  'records.links.delete',
  // Piece 21: the two login-creation capabilities; holding the first grants nothing of the second.
  'logins.createInternal',
  'logins.createExternal',
  // Piece 34, decision 1467: the HR group, its pages and their routes; and the policy text's reviewer, read by nothing yet.
  'hr.employees.view',
  'hr.policy.review',
  // Piece 29 with piece 8, decision 1904: Record time on a Work Order line; seeded on every internal role reading Work Orders, and taken away to limit a role to the clock.
  'workorder.time.typed',
  // Piece 19 with piece 8, decision 6004 (T2780 batch 2): Ask Claude, Resubmit and Make Report on a Deal's Discovery card; seeded on every internal role writing Deals.
  'deals.askClaude',
  // Piece 10 with piece 8, decision 10210 (T3514): changing a site's Tax exempt customer flag, by hand or by a site type's default; seeded on every internal role writing sites.
  'site.taxExempt.set',
  // Piece 9 with piece 8, decision 10340 (T801): releasing a file that no Deal owner, assigned rep or record owner answers for; seeded on Admin and Accountant.
  'files.releaseUnowned',
  // Piece 21 with piece 8, decision 11782 (T3708): rung on the General number and reading its calls and texts; seeded on Admin and the Salesperson role.
  'calls.answer',
] as const;

export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];

/**
 * 🔒 The role editor's label per key, held to the registry by its type: a key
 * added above without a label here does not compile (decision 10300).
 */
export const CAPABILITY_LABELS: Readonly<Record<CapabilityKey, StringKey>> = {
  'approvals.view': 'settings.roles.capability.approvals.view',
  'mail.quarantine.review': 'settings.roles.capability.mail.quarantine.review',
  'files.classify': 'settings.roles.capability.files.classify',
  'locations.edit': 'settings.roles.capability.locations.edit',
  'leads.review': 'settings.roles.capability.leads.review',
  'nda.review': 'settings.roles.capability.nda.review',
  'nda.activate': 'settings.roles.capability.nda.activate',
  'hr.orgchart.edit': 'settings.roles.capability.hr.orgchart.edit',
  'hr.hire': 'settings.roles.capability.hr.hire',
  'hr.discipline.deliver': 'settings.roles.capability.hr.discipline.deliver',
  'creditnote.receive': 'settings.roles.capability.creditnote.receive',
  'admin.operations': 'settings.roles.capability.admin.operations',
  'time.approve': 'settings.roles.capability.time.approve',
  'deals.commission.edit': 'settings.roles.capability.deals.commission.edit',
  'documents.inbox.review': 'settings.roles.capability.documents.inbox.review',
  'duplicates.review': 'settings.roles.capability.duplicates.review',
  'records.dataquality.review': 'settings.roles.capability.records.dataquality.review',
  'todos.role.view': 'settings.roles.capability.todos.role.view',
  'accounting.post': 'settings.roles.capability.accounting.post',
  'site.addForAnyAccount': 'settings.roles.capability.site.addForAnyAccount',
  'reports.projectperformance.view': 'settings.roles.capability.reports.projectperformance.view',
  'vendor.w9.override': 'settings.roles.capability.vendor.w9.override',
  'bom.costChange.override': 'settings.roles.capability.bom.costChange.override',
  'records.links.delete': 'settings.roles.capability.records.links.delete',
  'logins.createInternal': 'settings.roles.capability.logins.createInternal',
  'logins.createExternal': 'settings.roles.capability.logins.createExternal',
  'hr.employees.view': 'settings.roles.capability.hr.employees.view',
  'hr.policy.review': 'settings.roles.capability.hr.policy.review',
  'workorder.time.typed': 'settings.roles.capability.workorder.time.typed',
  'deals.askClaude': 'settings.roles.askClaude',
  'site.taxExempt.set': 'settings.roles.taxExempt',
  'files.releaseUnowned': 'settings.roles.capability.files.releaseUnowned',
  'calls.answer': 'settings.roles.capability.calls.answer',
};

export function isCapabilityKey(value: unknown): value is CapabilityKey {
  return typeof value === 'string' && (CAPABILITY_KEYS as readonly string[]).includes(value);
}

/** 🔒 Decision 10300: the refusal for a role save naming a key outside the registry, or null when every key is one. */
export function unknownCapabilityProblem(keys: readonly unknown[]): string | null {
  const unknown = keys.filter((key) => !isCapabilityKey(key)).map(String);
  return unknown.length === 0 ? null : fill(t('settings.roles.capabilityUnknown'), { keys: unknown.join(', ') });
}
